/**
 * Pure H OS · Sprint 2 · Global Overlays + keyboard listener.
 *
 * Mounts every platform-service overlay (Command Palette, Notification
 * Drawer, Shortcuts Modal) once at the app root and registers global
 * keyboard chords:
 *   ⌘K / Ctrl+K          → toggle Command Palette
 *   ⌘/ / Ctrl+/ / ?      → open Shortcuts help
 *   N                    → toggle Notification Drawer
 *   /                    → open palette (search-focused)
 *   G then D/N/A/R/I/P/S → navigate
 */
import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { usePlatform } from "../context/PlatformContext";
import CommandPalette from "./CommandPalette";
import NotificationDrawer from "./NotificationDrawer";
import ShortcutsModal from "./ShortcutsModal";

const NAV_MAP: Record<string, string> = {
  d: "/pureh/dashboard",
  f: "/pureh/finance",
  n: "/pureh/notifications",
  a: "/pureh/approvals",
  r: "/pureh/reports",
  i: "/pureh/ai",
  p: "/pureh/profile",
  s: "/pureh/settings",
};

function isTypingTarget(t: EventTarget | null): boolean {
  const el = t as HTMLElement | null;
  if (!el) return false;
  const tag = el.tagName;
  return tag === "INPUT" || tag === "TEXTAREA" || (el as any).isContentEditable === true;
}

export default function GlobalOverlays() {
  const platform = usePlatform();
  const navigate = useNavigate();
  const goTimer = useRef<number | null>(null);
  const goArmed = useRef(false);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const meta = e.metaKey || e.ctrlKey;

      // ⌘K / Ctrl+K → palette
      if (meta && e.key.toLowerCase() === "k") {
        e.preventDefault();
        platform.togglePalette();
        return;
      }

      // ⌘/ or ? → shortcuts help
      if ((meta && e.key === "/") || (e.key === "?" && !isTypingTarget(e.target))) {
        e.preventDefault();
        platform.openShortcuts();
        return;
      }

      // Esc handled by individual overlays.

      if (isTypingTarget(e.target)) return;

      // "/" → open palette
      if (e.key === "/") {
        e.preventDefault();
        platform.openPalette();
        return;
      }

      // "N" → toggle drawer
      if (e.key.toLowerCase() === "n" && !goArmed.current) {
        e.preventDefault();
        platform.toggleDrawer();
        return;
      }

      // "M" → mark all read
      if (e.key.toLowerCase() === "m" && !goArmed.current) {
        e.preventDefault();
        platform.markAllRead();
        return;
      }

      // Two-step G-then-X navigation
      if (e.key.toLowerCase() === "g") {
        goArmed.current = true;
        if (goTimer.current) window.clearTimeout(goTimer.current);
        goTimer.current = window.setTimeout(() => { goArmed.current = false; }, 900);
        return;
      }

      if (goArmed.current) {
        const target = NAV_MAP[e.key.toLowerCase()];
        goArmed.current = false;
        if (goTimer.current) { window.clearTimeout(goTimer.current); goTimer.current = null; }
        if (target) { e.preventDefault(); navigate(target); }
      }
    };

    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [platform, navigate]);

  return (
    <>
      <CommandPalette />
      <NotificationDrawer />
      <ShortcutsModal />
    </>
  );
}
